import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './ModalBase.module.css';

/**
 * Structural primitive for modal-like dialogs across the app.
 *
 * Handles ONLY the cross-cutting concerns: portal mount, escape-key close,
 * backdrop click-close, body scroll lock, and focus restoration. Visual
 * styling (gradients, headers, animations) is the wrapping component's job
 * — pass your own styled content as children.
 *
 * Migration target for the 16 modals catalogued in
 * docs/COMPONENT_DEDUP_REPORT.md > Family: Modals.
 */
export interface ModalBaseProps {
  isOpen: boolean;
  onClose: () => void;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
  overlayClassName?: string;
  children?: React.ReactNode;
}

const ModalBase = ({
  isOpen,
  onClose,
  closeOnEscape = true,
  closeOnBackdrop = true,
  ariaLabel,
  ariaLabelledBy,
  className = '',
  overlayClassName = '',
  children,
}: ModalBaseProps) => {
  const previousActiveElementRef = useRef<HTMLElement | null>(null);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose?.();
    },
    [closeOnBackdrop, onClose]
  );

  useEffect(() => {
    if (!isOpen) return undefined;

    if (closeOnEscape) {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose?.();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
    return undefined;
  }, [isOpen, closeOnEscape, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    previousActiveElementRef.current = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
      const prev = previousActiveElementRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={`${styles.overlay} ${overlayClassName}`}
      onClick={handleBackdropClick}
    >
      <div className={`${styles.content} ${className}`}>{children}</div>
    </div>,
    document.body
  );
};

export default ModalBase;
